import { FC, MutableRefObject, useEffect, useRef, } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import ReactDOM from "react-dom";

import i18n from "../../../i18n/config";
import { getCurrentUser, getIsOrganizationUser, getPendingReceivedApplicationsCount, setCurrentUser } from "../../../features/basic/reducers/basicReducer";
import { CloseIcon } from "../modal/modalIcons";
import { SecondaryButton } from "../../../styles/basicComponents";
import { useTheme, useVariables } from "../../../features/context/CustomerContext";
import useCustomTranslation from "../../../features/hooks/CustomUseTranslation";
import { LocalizationLanguage } from "../../../customerVariables/variable_interfaces";
import { useAuth } from "react-oidc-context";

const MobileMenuWrapper = styled.div`
    width: 280px;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 99;
`
const MobileMenuContent = styled.div`
    padding: 20px;
    display: grid;
    text-align-last: left;
    background-color: ${props => props.theme.brandColor1};
    margin-bottom: 80px;
`
const MobileMenuHeader = styled.p`
    font-weight: bold;
    color: white;
    margin-top: 40px;
    margin-bottom: 20px;
`
const MobileMenuLinkItem = styled.a`
    font-size: 12px;
    color: white;
    text-decoration: none;
    line-height: 30px;
    margin-top: 6px;
    margin-bottom: 6px;
`
const MobileMenuNavLinkItem = styled(NavLink)`
    font-size: 12px;
    color: white;
    text-decoration: none;
    line-height: 30px;
    margin-top: 6px;
    margin-bottom: 6px;
`
const ButtonWrapper = styled.div`
    text-align: center;
`
const LogoutButton = styled(SecondaryButton)`
    height: 30px;
    width: 100%;
    margin: 30px 0 0 0;
    box-shadow: none;
    text-align: center;
    padding-left: 75px;
`
export interface MobileMenuProps {
    show: boolean;
    showModal: (show: boolean) => void;
    languageSelection: boolean;
    setLanguageSelection: (show: boolean) => void;
}

const MobileMenu: FC<MobileMenuProps> = (props) => {
    const auth = useAuth();
    const { t } = useCustomTranslation();
    const theme = useTheme();
    const variables = useVariables();
    
    const currentUser = useSelector(getCurrentUser);
    const isOrganizationUser= useSelector(getIsOrganizationUser);
    const pendingReceivedApplicationsCount = useSelector(getPendingReceivedApplicationsCount);
    const wrapperRef = useRef(null);
    CheckOutsideInfoBoxClicks(wrapperRef);
    
    if (!props.show) {
        return null;
    }

    function changeLanguage(e: string) {
        i18n.changeLanguage(e);
        props.setLanguageSelection(false);
    }
    
      // This function is used to close the profile menu when clicking outside it in the mobile view.
      function CheckOutsideInfoBoxClicks(ref: MutableRefObject<null>) {
        useEffect(() => {
            function handleClickOutside(event: any) {
                const profileMenu = ref?.current;
                if (profileMenu && event.target && !ReactDOM.findDOMNode(profileMenu)?.contains(event.target)){
                    props.showModal(false);
                }
            }
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref]);
    }

    function logout() {
        auth.removeUser();
        setCurrentUser(undefined);
        auth.signoutRedirect({ id_token_hint: auth.user?.id_token });
        window.sessionStorage.removeItem("keycloakIdToken");
    }

    return (
        <MobileMenuWrapper ref={wrapperRef}>
            <MobileMenuContent theme={theme}>
                <CloseIcon onClick={() => props.showModal(false)}/>
                {props.languageSelection ?
                    <>
                        <MobileMenuHeader>{t("Choose language")}</MobileMenuHeader>
                        {variables.localizationLanguages && variables.localizationLanguages?.map((language: LocalizationLanguage) => (
                            <MobileMenuLinkItem key={language.language} onClick={() => changeLanguage(language.languageAbbreviation)} >{t(language.language)}</MobileMenuLinkItem>))}
                    </>
                    :<>
                        <MobileMenuHeader>{t("Manage authorizations")}</MobileMenuHeader>
                        <MobileMenuNavLinkItem to="authorizedUsers" onClick={() => props.showModal(false)}>{t("authorizedUsers")}</MobileMenuNavLinkItem>
                        <MobileMenuNavLinkItem to="customerAuthorizations" onClick={() => props.showModal(false)}>{t("customerAuthorizations")}</MobileMenuNavLinkItem>
                        <MobileMenuNavLinkItem to="receivedApplications" onClick={() => props.showModal(false)}>{t("receivedApplications")}{pendingReceivedApplicationsCount && pendingReceivedApplicationsCount > 0 ? " (" + pendingReceivedApplicationsCount + ")" : ""}</MobileMenuNavLinkItem>
                        <MobileMenuNavLinkItem to="sentApplications" onClick={() => props.showModal(false)}>{t("sentApplications")}</MobileMenuNavLinkItem>
                        {isOrganizationUser && 
                            <MobileMenuNavLinkItem to="organizations" onClick={() => props.showModal(false)}>{t("organizations")}</MobileMenuNavLinkItem>}
                        <MobileMenuNavLinkItem to="selectApplicationType" onClick={() => props.showModal(false)}>{t("createApplication")}</MobileMenuNavLinkItem>
                        
                        <MobileMenuHeader>{t("externalServices")}</MobileMenuHeader>
                        {variables.headerMenuLinks && variables.headerMenuLinks?.map((link: any, index: number) => (
                            <MobileMenuLinkItem key={index} href={link.buttonLink}>{t(link.buttonTitle)}</MobileMenuLinkItem>
                        ))}

                        <MobileMenuHeader>{currentUser?.firstName + " " + currentUser?.lastName}</MobileMenuHeader>
                        {variables.localizationLanguages.length > 1 && 
                            <MobileMenuLinkItem onClick={() => props.setLanguageSelection(true)} >{t('Language, ' + i18n.language)}</MobileMenuLinkItem>}
                        <MobileMenuLinkItem href={variables.USER_SETTINGS_LINK}>{t("User settings")}</MobileMenuLinkItem>
                        <ButtonWrapper>
                            <LogoutButton onClick={() => logout()}>{t("Logout")}</LogoutButton>
                        </ButtonWrapper>
                    </>}
            </MobileMenuContent>
        </MobileMenuWrapper>
    )
}

export default MobileMenu;
